import {
    ArrayUtil, StringUtil
} from "@shopliftr/common-js/data";
import {
    APP_FEATURE_GROUP_ID, NG_ROUTE_PARAM_REGEX, NG_ROUTE_PATH_DIVIDER, NG_ROUTER_ROOT_COMMAND
} from "../constants/app-constants";
import { ISidenavLink } from "../../iapi-application/interfaces/sidenav-link";

/**
 * A map of RouteIds to RouteNavCommandData
 * One of these will be provided for each Feature Group and the App
 *
 * The router services will use these to determine the Router navigation commands for given route ids
 */
export type FeatureGroupNavCommandData = ReadonlyMap<string, RouteNavCommandData>;

/**
 * Metadata used to determine Router Nav Commands and paths
 *
 * The route command data is relative to the Feature Group
 *
 * Unless the this is an App Route in which case it is relative to the Router Root
 */
export class RouteNavCommandData {

    /**
     * The link used in the sidenav
     */
    readonly sidenavLink?: Readonly<ISidenavLink>;

    /**
     * The required params used to produce Router Navigation Commands
     *
     * This is calculated based on the routes path, if there are no required params, the set is empty
     *
     * For Example
     * The path "campaign/:campaignId/ad-unit"/:adUnitId/list"
     * will have the following require params:
     * ["campaignId", "adUnitId"]
     */
    readonly requiredParams: ReadonlySet<string>;

    /**
     * Path segments a list of atomic path segments
     *
     * Segments are relative to the feature group, unless this is an appRoute in which case it is relative to the Router Root
     *
     * i.e.
     * App
     * ["/", "designer"]
     *
     * Feature Group
     * ["campaign", ":campaignId", "ad-unit", ":adUnitId", "list"]
     */
    private readonly _pathSegments: ReadonlyArray<string>;

    /**
     * Returns true if this command is an App Route
     *
     * An app route is loaded during application bootstrap and is relative to the Router's Root
     *
     * If its false the command is for a Feature Group
     */
    get appRoute(): boolean {

        return this.featureGroupId === APP_FEATURE_GROUP_ID;
    }

    /**
     * Creates a RouteNavCommand
     * If routeId, featureGroupId or routePathSegments are not included
     * an error will be thrown
     *
     * routePathsSegments are the array of path fields for the Route and its parents
     * relative to the Feature Group or in the case of an appRoute the Router root
     *
     * i.e. ["campaign/:campaignId", "ad-unit/:adUnitId", "list"]
     *
     * These routePathSegments will be used to populate requireParams and to generate the Router Navigation Commands
     */
    constructor(

        /**
         * The route id for this route
         */
        public readonly routeId: string,

        /**
         * The feature group id for this route
         */
        public readonly featureGroupId: string,
        routePathSegments: ReadonlyArray<string>,
        sidenavLink?: Partial<ISidenavLink>
    ) {

        if (!routeId) {
            throw new Error("Route id is required");
        }
        if (!featureGroupId) {
            throw new Error("Feature group id is required");
        }
        if (!routePathSegments) {
            throw new Error("routePathSegments is required");
        }

        let pathSegments = new Array<string>();
        if (this.appRoute) {
            // App route use relative to the root of the Router
            pathSegments.push(NG_ROUTER_ROOT_COMMAND);
        }

        // We want to make sure that each path segments is atomic
        // ie. ["campaign/:campaignId", "ad-unit/:adUnitId", "list"] -> ["product", ":upc", "edit"]
        this._pathSegments = [...pathSegments, ...routePathSegments.flatMap((segment) => segment.split(NG_ROUTE_PATH_DIVIDER))]
            .filter((value) => !StringUtil.emptyString(value));

        // Determine the required params by looking for path params in root
        // i.e. ["campaign", ":campaignId", "ad-unit", ":adUnitId", "list"]
        // Will have required params ["campaignId", "adUnitId"]
        const requiredParams = new Set<string>();
        this._pathSegments.forEach((segment) => {
            const matches = segment.match(NG_ROUTE_PARAM_REGEX);
            if (matches) {
                requiredParams.add(matches.groups.param);
            }
        });
        this.requiredParams = requiredParams;

        if (sidenavLink) {

            if (!ArrayUtil.isEmpty(this.requiredParams)) {
                throw new Error("route with sidenavLink cannot have path params");
            }

            this.sidenavLink = {
                ...sidenavLink as ISidenavLink,
                commands: this.getCommands() as Array<string>
            };
        }
    }

    /**
     * This returns the Angular Router Navigation Commands used in the Router Navigate function
     *
     * The params field must contain required params for this route and any parent RouteNavCommandData provided
     * or an error will be thrown
     *
     * i.e.
     * {
     *    campaignId: "83c31853-9f3f-4768-86d6-00893d5afc40",
     *    adUnitId: "4e8dbbe-820f-4e83-829b-6351d00b2ff2"
     * }
     *
     * When parent is provided the path will be relative to those parents in index order i.e.
     * parent [RouteNavCommand for App Route "designer"];
     * ["/", "designer", "campaign", "83c31853-9f3f-4768-86d6-00893d5afc40", "ad-unit", "e4e8dbbe-820f-4e83-829b-6351d00b2ff2", "list"]
     *
     * If the parents are not provided the command will be relative the FeatureGroup.
     * i.e. ["campaign", "83c31853-9f3f-4768-86d6-00893d5afc40", "ad-unit", "e4e8dbbe-820f-4e83-829b-6351d00b2ff2", "list"]
     *
     * The parents field is not allowed on appRoutes and error will be thrown if it is provided
     *
     */
    getCommands(params?: Record<string, string>, parents?: ReadonlyArray<RouteNavCommandData>): ReadonlyArray<string> {

        if (this.appRoute && !ArrayUtil.isEmpty(parents)) {
            throw new Error(`parents field is not allowed. Route ${this.routeId} is an Application route, and cannot have parents.`);
        }
        const parentCommands = parents?.flatMap((command) => command.getCommands(params)) ?? [];
        const commands = this._pathSegments.map((segment) => {
            return segment.replace(NG_ROUTE_PARAM_REGEX, (match) => {
                // Ignore the prefix from the path;
                const paramName = match.substring(1);
                if (!params?.[paramName]) {
                    throw new Error(`Missing required path param ${paramName}`);
                }
                return params[paramName];
            });
        });
        return [...parentCommands, ...commands];
    }

    /**
     * This returns the Angular Router Navigation path used in to create a UrlTree
     *
     * The params field must contain required params for this route and any parent RouteNavCommandData provided
     * or an error will be thrown
     *
     * i.e.
     * {
     *    campaignId: "83c31853-9f3f-4768-86d6-00893d5afc40",
     *    adUnitId: "4e8dbbe-820f-4e83-829b-6351d00b2ff2"
     * }
     *
     * When parent is provided the path will be relative to those parents in index order i.e.
     * parent [RouteNavCommand for App Route "designer"];
     * "/designer/campaign/83c31853-9f3f-4768-86d6-00893d5afc40/ad-unit/e4e8dbbe-820f-4e83-829b-6351d00b2ff2/list"
     *
     * If the parents are not provided the command will be relative the FeatureGroup.
     * i.e. "campaign/83c31853-9f3f-4768-86d6-00893d5afc40/ad-unit/e4e8dbbe-820f-4e83-829b-6351d00b2ff2/list"
     *
     * The parents field is not allowed on appRoutes and error will be thrown if it is provided
     *
     */
    getPath(params?: Record<string, string>, parents?: ReadonlyArray<RouteNavCommandData>): string {

        return this.getCommands(params, parents).join(NG_ROUTE_PATH_DIVIDER);
    }
}
